import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { API_BASE_URL } from './apiConfig';

/**
 * Unified API hook. Returns fetchWithAuth for authenticated requests.
 * Supports aborting requests, flexible error handling, and response type selection.
 */
export const useApi = () => {
  const { token, user, loading } = useAuth();
  const { impersonation } = useImpersonation();

  /**
   * Authenticated API call
   * @param endpoint: string - API endpoint (appended to base URL)
   * @param options: RequestInit - fetch options (method, headers, body, etc.)
   * @param responseType: 'json' | 'text' | 'blob' - How to return the response
   * @param abortSignal: AbortSignal - optional signal for cancelling
   */
  const fetchWithAuth = useCallback(async (
    endpoint: string,
    options: RequestInit = {},
    responseType: 'json' | 'text' | 'blob' = 'json',
    abortSignal?: AbortSignal
  ) => {
    // Authentication checks
    if (loading) throw new Error('Authentication is still loading. Please wait.');
    if (!user) throw new Error('Not authenticated. Please log in.');
    if (!token) throw new Error('No authentication token available. Please log in again.');

    const url = `${API_BASE_URL}${endpoint}`;
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers as object,
    };

    // Super-admin "browse as gym": tag CRM calls so the backend re-maps the
    // request to the impersonated gym's owner. Never applied to /api/saas/*
    // (those authenticate as the super admin directly).
    if (impersonation && !endpoint.startsWith('/api/saas')) {
      headers['X-Impersonate-Gym-Id'] = impersonation.gymId;
      if (impersonation.edit) headers['X-Impersonate-Edit'] = '1';
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: abortSignal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || 'API request failed';
        } catch {
          errorMessage = errorText || 'API request failed';
        }
        if (response.status === 401) throw new Error('Session expired. Please log in again.');
        throw new Error(errorMessage);
      }

      if (responseType === 'json') return await response.json();
      if (responseType === 'blob') return await response.blob();
      if (responseType === 'text') return await response.text();

    } catch (error) {
      // Log only if in dev mode (replace with actual env check)
      if (process.env.NODE_ENV !== 'production') {
        if (error instanceof Error &&
          !error.message.includes('authentication') &&
          !error.message.includes('loading')
        ) {
          console.error('API Call Error:', error);
        }
      }
      throw error;
    }
  }, [token, user, loading, impersonation]);

  return { fetchWithAuth };
};